import classNames from "./Header.module.css";
import logo from "../assets/logo_with_text.svg";
import logoDark from "../assets/logo_witth_text_dark.svg";
import useUser from "../hooks/user";
import { Link, useNavigate } from "react-router-dom";
import {
  IoChevronDown,
  IoChevronUp,
  IoHeadsetOutline,
  IoSettingsOutline,
  IoVideocamOutline,
  IoWalletOutline,
} from "react-icons/io5";
import * as Popover from "@radix-ui/react-popover";
import { ReactNode, useCallback, useState } from "react";
import { FiLogOut } from "react-icons/fi";
import { BsClock } from "react-icons/bs";
import ConfirmActionDialog from "./common/dialogs/ConfirmActionDialog";
import { useLogEvent } from "../hooks/log";
import { useMeeting } from "../hooks/meeting";
import Button from "./common/Button";
import meetingImg from "../assets/stock/meeting.webp";
import eventImg from "../assets/stock/event.webp";
import integrationImg from "../assets/stock/integration.webp";
import { useIsMobile } from "../hooks/layout";
import { PlatformBadges } from "./routes/home/Home";
import { LuMenu } from "react-icons/lu";
import { getAuth } from "firebase/auth";

const LogoutDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const [, setUser] = useUser();
  const navigate = useNavigate();
  const logEvent = useLogEvent();

  const logout = useCallback(async () => {
    // Try to sign out of firebase
    try {
      const auth = getAuth();
      await auth.signOut();
    } catch (error) {
      // Ignore errors
      console.warn(error);
    }

    localStorage.clear();
    setUser((prevState) => ({
      ...prevState,
      auth: undefined,
    }));
    navigate("/");
    logEvent({
      type: "logout",
    });
    onOpenChange(false);
  }, [setUser, navigate, logEvent, onOpenChange]);

  return (
    <ConfirmActionDialog
      title="Log out"
      open={open}
      onOpenChange={onOpenChange}
      onConfirm={logout}
      action="Log out"
      content={<h3>Are you sure you want to log out?</h3>}
    />
  );
};

function UserPopover(props: { name: string }) {
  const [open, setOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  return (
    <>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger className={classNames.UserPopoverTrigger}>
          Hi, {props.name.includes(" ") ? props.name.split(" ")[0] : props.name}
          {!open ? <IoChevronDown /> : <IoChevronUp />}
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className={classNames.UserPopoverContent}
            sideOffset={8}
            align="end"
            onClick={() => setOpen(false)}
          >
            <Link
              to="/meetings"
              target="_blank"
              className={classNames.OnlyOnMobile}
            >
              <IoVideocamOutline />
              Meetings & events
            </Link>
            <Link
              to="/settings"
              target="_blank"
              className={classNames.HideOnMobile}
            >
              <IoSettingsOutline />
              Settings
            </Link>
            <Link
              to="/billing"
              target="_blank"
              className={classNames.OnlyOnMobile}
            >
              <IoWalletOutline />
              Plan & billing
            </Link>
            <Link to="/usage" target="_blank">
              <BsClock />
              Usage
            </Link>
            <Link to="/support" target="_blank">
              <IoHeadsetOutline />
              Support
            </Link>
            <button onClick={() => setLogoutDialogOpen(true)}>
              <FiLogOut />
              Log out
            </button>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      <LogoutDialog
        open={logoutDialogOpen}
        onOpenChange={setLogoutDialogOpen}
      />
    </>
  );
}

function ProductsPopover() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const logEvent = useLogEvent();
  const clickGetADemo = useCallback(() => {
    logEvent({ type: "click_get_a_demo_header" });
    window.open("https://calendly.com/snow-huo-byrdhouse/30min", "_blank");
  }, [logEvent]);
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger className={classNames.ProductsPopoverTrigger} asChild>
        <Button
          color="black"
          backgroundColor="transparent"
          borderColor="transparent"
        >
          Products
          {!open ? <IoChevronDown /> : <IoChevronUp />}
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className={classNames.ProductsPopoverContent}
          sideOffset={8}
          align="center"
        >
          <h2>Products</h2>
          <div className={classNames.Products}>
            <div>
              <img src={meetingImg} alt="Online meetings with Langfinity" />
              <div>
                <h2>Online meetings with Langfinity</h2>
                <PlatformBadges platforms={["langfinity"]} />
                <p>
                  Connect with your global team in any language with just one
                  click
                </p>
              </div>
              <Button
                color="white"
                backgroundColor="orange"
                borderColor="orange"
                onClick={() => navigate("/signup")}
              >
                Try for free
              </Button>
            </div>

            <div>
              <img src={eventImg} alt="In-person events or conferences" />
              <div>
                <h2>In-person events or conferences</h2>
                <PlatformBadges platforms={["langfinity"]} />
                <p>
                  Scan a QR code to unlock real-time translation in 50+
                  languages for events
                </p>
              </div>
              <Button
                color="white"
                backgroundColor="orange"
                borderColor="orange"
                onClick={clickGetADemo}
              >
                Get a demo
              </Button>
            </div>

            <div>
              <img
                src={integrationImg}
                alt="Translation for third-party platforms"
              />
              <div>
                <h2>Translation for third-party platforms</h2>
                <PlatformBadges platforms={["teams", "meet", "zoom"]} />
                <p>
                  Get real-time translation in Teams, Zoom, and Google Meet with
                  a single tap
                </p>
              </div>
              <Button
                color="white"
                backgroundColor="orange"
                borderColor="orange"
                onClick={clickGetADemo}
              >
                Get a demo
              </Button>
            </div>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

const ResourcesPopover = () => {
  const [open, setOpen] = useState(false);
  const logEvent = useLogEvent();
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger className={classNames.ResourcesPopoverTrigger} asChild>
        <Button
          color="black"
          backgroundColor="transparent"
          borderColor="transparent"
        >
          Resources
          {!open ? <IoChevronDown /> : <IoChevronUp />}
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className={classNames.ResourcesPopoverContent}
          sideOffset={8}
          align="center"
        >
          <Link to="/faq">FAQ</Link>
          <Link to="/customer-stories">Customer stories</Link>
          <Link
            to="https://langfinity.ai/blog/"
            target="_blank"
            onClick={() => {
              logEvent({ type: "click_blog_header" });
            }}
          >
            Blog
          </Link>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

const MobileMenuPopover = () => {
  const [open, setOpen] = useState(false);
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger className={classNames.MobileMenuPopoverTrigger} asChild>
        <Button
          color="black"
          borderColor="transparent"
          backgroundColor="transparent"
        >
          <LuMenu />
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className={classNames.MobileMenuPopoverContent}
          sideOffset={8}
          align="end"
        >
          <Link to="/faq">FAQ</Link>
          <Link to="/customer-stories">Customer stories</Link>
          <Link to="/about">Company</Link>
          <Link to="/pricing">Pricing</Link>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

function Header(props: {
  hideUserNav?: boolean;
  disableLogoClick?: boolean;
  landing?: boolean;
  className?: string;
  children?: ReactNode;
}) {
  const [user] = useUser();
  const [meeting] = useMeeting();

  const clickGetADemo = useCallback(() => {
    window.open("https://calendly.com/snow-huo-byrdhouse/30min", "_blank");
  }, []);

  const isMobile = useIsMobile();

  if (meeting.joined) {
    return null;
  }

  if (props.landing) {
    return (
      <div className={`${classNames.LandingHeader} ${props.className ?? ""}`}>
        <Link to="/" className={classNames.Logo}>
          <img src={logoDark} alt="Langfinity" />
        </Link>
        <div>
          <nav>
            {!isMobile && (
              <>
                <ProductsPopover />
                <ResourcesPopover />
                <Link to="/about">Company</Link>
                <Link to="/pricing">Pricing</Link>
              </>
            )}
            <Link to="/login">Log in</Link>
          </nav>
          {!isMobile && (
            <Button
              color="white"
              borderColor="blue"
              backgroundColor="blue"
              size="landing-header"
              onClick={clickGetADemo}
            >
              Get a demo
            </Button>
          )}
          {isMobile && <MobileMenuPopover />}
        </div>
      </div>
    );
  }

  return (
    <div className={`${classNames.Header} ${props.className ?? ""}`}>
      {props.disableLogoClick ? (
        <div className={classNames.Logo}>
          <img src={logo} alt="Langfinity" />
        </div>
      ) : (
        <Link to="/" className={classNames.Logo}>
          <img src={logo} alt="Langfinity" />
        </Link>
      )}
      {!props.hideUserNav && user.auth ? (
        <UserPopover name={user.auth.name} />
      ) : null}
      {props.children}
    </div>
  );
}

export default Header;
